import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    useMaterialReactTable,
    MaterialReactTable,
} from 'material-react-table';
import { Box, Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import apiService from '../../services/apiService';
import { formatPlayerName } from '../../utils/formatters';
import {useTableUrlState} from "../../hooks/useTableUrlState"; // Assuming player object is available

const GameSessionsPage = () => {
    // Data state from the paginated API
    const [data, setData] = useState([]);
    const [rowCount, setRowCount] = useState(0);

    // API fetching state
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    const {pagination, setPagination, sorting, setSorting} = useTableUrlState({
        initialSorting: [{id: 'createdAt', desc: false}],
        initialPageSize: 20
    });

    // The core data fetching logic
    const fetchData = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams({
            page: pagination.pageIndex,
            size: pagination.pageSize,
        });

        if (sorting.length > 0) {
            params.append('sort', `${sorting[0].id},${sorting[0].desc ? 'desc' : 'asc'}`);
        }

        apiService.get(`/api/game-sessions?${params.toString()}`)
            .then(page => {
                setData(page.content);
                setRowCount(page.totalElements);
                setIsError(false);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [pagination, sorting]);

    // Trigger fetch whenever pagination or sorting changes
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Define table columns
    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'Session ID',
                size: 300,
                Cell: ({ cell }) => (
                    <Link component={RouterLink} to={`/game-sessions/${cell.getValue()}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorFn: (row) => row.player ? formatPlayerName(row.player) : row.playerId,
                id: 'playerUid',
                header: 'Player',
                Cell: ({ row }) => {
                    const { player, playerId } = row.original;
                    // Handle cases where the full player object might not be present
                    const target = player ? `/players/${player.id}` : `/players/${playerId}`;
                    const text = player ? formatPlayerName(player) : playerId;
                    return <Link component={RouterLink} to={target}>{text}</Link>;
                }
            },
            {
                accessorKey: 'categoryId',
                header: 'Category',
                Cell: ({ cell }) => (
                    <Link component={RouterLink} to={`/categories/${cell.getValue()}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorKey: 'machineId',
                header: 'Machine',
                Cell: ({ cell }) => (
                    <Link component={RouterLink} to={`/machines/${cell.getValue()}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorKey: 'createdAt',
                header: 'Created At',
                Cell: ({ cell }) => new Date(cell.getValue()).toLocaleString(),
            },
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data,
        // Server-side configuration
        manualPagination: true,
        manualSorting: true,
        rowCount,
        // State management
        state: {
            pagination,
            sorting,
            isLoading,
            showAlertBanner: isError,
        },
        // State update handlers
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        // UI configuration
        muiToolbarAlertBannerProps: isError
            ? { color: 'error', children: 'Error loading data' }
            : undefined,
        initialState: {
            showGlobalFilter: true, // Allow users to search the current page
        },
    });

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom>
                Game Sessions
            </Typography>
            <MaterialReactTable table={table} />
        </Box>
    );
};

export default GameSessionsPage;